import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, Loader2, UserPlus, Pencil, Square, CheckCircle2, MapPin, Briefcase, Link2, Copy, X } from 'lucide-react';
import { atlasApi, AtlasUser, Vaga, VagaCandidato, VagaEtapa } from '../shared/atlasApi';
import ModalCandidato from './ModalCandidato';
import RelatorioIndividual from '../DISC/RelatorioIndividual';

interface Props {
  user: AtlasUser;
  vagaId: string;
  podeEditar: boolean;
  onVoltar: () => void;
  onEditar: (v: Vaga) => void;
}

const ETAPAS: { id: VagaEtapa; label: string }[] = [
  { id: 'triagem', label: 'Triagem' },
  { id: 'entrevista_rh', label: 'Entrevista RH' },
  { id: 'entrevista_gestor', label: 'Entrevista Gestor' },
  { id: 'teste', label: 'Teste' },
  { id: 'proposta', label: 'Proposta' },
  { id: 'aprovado', label: 'Aprovado' },
  { id: 'reprovado', label: 'Reprovado' },
  { id: 'desistiu', label: 'Desistiu' },
];

const FATOR_COR: Record<string, string> = { D: 'bg-rose-500', I: 'bg-amber-400', S: 'bg-emerald-500', C: 'bg-sky-500' };

function fmtData(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

const Detalhe: React.FC<Props> = ({ user, vagaId, podeEditar, onVoltar, onEditar }) => {
  const [vaga, setVaga] = useState<Vaga | null>(null);
  const [candidatos, setCandidatos] = useState<VagaCandidato[]>([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [modal, setModal] = useState(false);
  const [discLink, setDiscLink] = useState<{ nome: string; link: string; expira_em: string } | null>(null);
  const [discGerando, setDiscGerando] = useState<string | null>(null);
  const [discVer, setDiscVer] = useState<VagaCandidato | null>(null);
  const [copiado, setCopiado] = useState(false);

  const gerarDisc = async (c: VagaCandidato) => {
    setDiscGerando(c._id); setErro(null);
    try {
      const r = await atlasApi.discCandidato(user, vagaId, c._id);
      if (r.item.link) setDiscLink({ nome: c.nome, link: r.item.link, expira_em: r.item.expira_em });
      carregar();
    } catch (e: any) { setErro(e.message || 'Erro ao gerar o link'); }
    finally { setDiscGerando(null); }
  };

  const copiar = async (texto: string) => {
    try { await navigator.clipboard.writeText(texto); setCopiado(true); setTimeout(() => setCopiado(false), 2000); } catch { /* sem clipboard */ }
  };

  const carregar = useCallback(() => {
    setLoading(true);
    atlasApi.getVaga(user, vagaId)
      .then(r => { setVaga(r.vaga); setCandidatos(r.candidatos || []); })
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user, vagaId]);

  useEffect(() => { carregar(); }, [carregar]);

  const mover = async (candId: string, etapa: VagaEtapa) => {
    setDrag(null);
    const cand = candidatos.find(c => c._id === candId);
    if (!cand || cand.etapa === etapa) return;
    setCandidatos(cs => cs.map(c => c._id === candId ? { ...c, etapa } : c)); // otimista
    try { await atlasApi.atualizarCandidato(user, vagaId, candId, { etapa }); }
    catch (e: any) { setErro(e.message || 'Erro ao mover'); carregar(); }
  };

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>;
  if (erro && !vaga) return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>;
  if (!vaga) return null;

  return (
    <div className="space-y-4">
      <button onClick={onVoltar} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1"><ChevronLeft className="w-4 h-4" /> Voltar</button>

      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900">{vaga.titulo}</h2>
            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
              <span className="flex items-center gap-1"><Briefcase className="w-4 h-4" /> {vaga.tipo_contrato?.toUpperCase()}</span>
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {vaga.modalidade}</span>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600">{vaga.status}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setModal(true)} className="px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> {podeEditar ? 'Candidato' : 'Indicar'}
            </button>
            {podeEditar && (
              <>
                <button onClick={() => onEditar(vaga)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2"><Pencil className="w-4 h-4" /> Editar</button>
                {vaga.status !== 'encerrada' && (
                  <button onClick={() => atlasApi.encerrarVaga(user, vagaId).then(carregar)} className="px-3 py-2 rounded-lg border border-rose-200 text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2"><Square className="w-4 h-4" /> Encerrar</button>
                )}
              </>
            )}
          </div>
        </div>
        {vaga.descricao && <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap">{vaga.descricao}</p>}
        {vaga.requisitos && <div className="mt-3"><div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Requisitos</div><p className="text-sm text-gray-700 whitespace-pre-wrap">{vaga.requisitos}</p></div>}
        {vaga.beneficios && <div className="mt-3"><div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Benefícios</div><p className="text-sm text-gray-700 whitespace-pre-wrap">{vaga.beneficios}</p></div>}
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {/* Kanban (RH) */}
      {podeEditar && (
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-3 min-w-max">
            {ETAPAS.map(et => {
              const lista = candidatos.filter(c => c.etapa === et.id);
              return (
                <div
                  key={et.id}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => drag && mover(drag, et.id)}
                  className="w-56 flex-shrink-0 bg-gray-50 rounded-2xl border border-gray-200 p-2"
                >
                  <div className="px-2 py-1 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-600">{et.label}</span>
                    <span className="text-[10px] text-gray-400">{lista.length}</span>
                  </div>
                  <div className="space-y-2 min-h-[40px]">
                    {lista.map(c => (
                      <div
                        key={c._id}
                        draggable
                        onDragStart={() => setDrag(c._id)}
                        onDragEnd={() => setDrag(null)}
                        className="bg-white rounded-xl border border-gray-200 p-2.5 cursor-grab active:cursor-grabbing hover:border-purple-300 shadow-sm"
                      >
                        <div className="text-sm font-medium text-gray-900 truncate">{c.nome}</div>
                        {c.email && <div className="text-[11px] text-gray-400 truncate">{c.email}</div>}
                        <div className="text-[10px] text-gray-400 mt-1">{c.origem}{c.curriculo_url ? ' · CV' : ''}</div>
                        {c.disc?.perfil ? (
                          <button onClick={() => setDiscVer(c)} className="mt-2 w-full flex items-center gap-1.5 px-2 py-1 rounded-lg bg-purple-50 text-purple-700 text-[11px] font-medium hover:bg-purple-100">
                            <span className={`w-2 h-2 rounded-full ${FATOR_COR[c.disc.perfil.estilo_principal] || 'bg-gray-400'}`} />
                            DISC: {c.disc.perfil.estilo_principal}{c.disc.perfil.estilo_secundario} · ver
                          </button>
                        ) : c.disc_link ? (
                          <button onClick={() => setDiscLink({ nome: c.nome, link: c.disc_link!.link, expira_em: c.disc_link!.expira_em })} className="mt-2 w-full flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 text-amber-700 text-[11px] font-medium hover:bg-amber-100">
                            <Link2 className="w-3 h-3" /> DISC aguardando · copiar link
                          </button>
                        ) : (
                          <button onClick={() => gerarDisc(c)} disabled={discGerando === c._id} className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1 rounded-lg border border-purple-200 text-purple-700 text-[11px] font-medium hover:bg-purple-50 disabled:opacity-50">
                            {discGerando === c._id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Link2 className="w-3 h-3" />} Gerar link DISC
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!podeEditar && (
        <div className="bg-white rounded-2xl border border-gray-200 p-6 text-sm text-gray-500 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Conhece alguém para esta vaga? Use "Indicar" acima.
        </div>
      )}

      {discLink && (
        <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDiscLink(null)}>
          <div className="bg-white max-w-lg w-full rounded-2xl shadow-2xl p-5 space-y-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-gray-900 flex-1">Link do DISC · {discLink.nome}</h3>
              <button onClick={() => setDiscLink(null)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-gray-600">Mande este link para o candidato (WhatsApp, e-mail). Ele responde sem login e vê só um "obrigado"; o resultado aparece aqui no card. Vale até <strong>{fmtData(discLink.expira_em)}</strong> e serve para uma resposta.</p>
            <div className="flex gap-2">
              <input readOnly value={discLink.link} onFocus={e => e.target.select()} className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 bg-gray-50" />
              <button onClick={() => copiar(discLink.link)} className="px-3 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-1.5">
                <Copy className="w-4 h-4" /> {copiado ? 'Copiado!' : 'Copiar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {discVer?.disc?.perfil && (
        <div className="fixed inset-0 z-[10050] bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setDiscVer(null)}>
          <div className="bg-gray-50 max-w-5xl w-full rounded-2xl shadow-2xl p-4 sm:p-5 space-y-4 my-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <h3 className="font-bold text-gray-900">DISC · {discVer.nome}</h3>
                <div className="text-xs text-gray-500">Respondido em {fmtData(discVer.disc.respondido_em)} · {vaga.titulo}</div>
              </div>
              <button onClick={() => setDiscVer(null)} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <RelatorioIndividual user={user} candidato={{ vagaId, candId: discVer._id }} />
          </div>
        </div>
      )}

      {modal && (
        <ModalCandidato user={user} vagaId={vagaId} modoIndicacao={!podeEditar} onClose={() => setModal(false)} onSalvo={() => { setModal(false); carregar(); }} />
      )}
    </div>
  );
};

export default Detalhe;
